import { useContext } from "react";
import { Divider, Grid } from "@mui/material";
import { convertToJalali } from "../../../utils/date";
import { numberWithCommas } from "../../../utils/utility";
import { LocaleContext } from "../../../routing/LangRouter";
import { useTranslation } from "react-i18next";
import TicketArrow from "../ticket-arrow/ticket-arrow";

//import styles
import "./minimal-flight-card.scss";

//import utils
import { flightDataMapper } from "../../../utils/flightDataMapper";

//import image
import AirplaneSvg from "../../../assets/icons/airplane.svg";

function FlightCard({ flightItem, isClickable, handleClick, type, name, handleDelete }) {
    //general
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const isTwoWay = domesticFlightInformation?.returnDate;
    const normalizedFlightItem = type === "suggest" ? flightDataMapper(isTwoWay, flightItem) : flightItem;
    const {
        persianType,
        flightNumber,
        cabinPersian,
        cabin,
        aircraft,
        airlineLogo,
        airline,
        airlinePersian,
        departureTime,
        departureEnName,
        departureFaName,
        destinationTime,
        departureDate,
        destinationEnName,
        destinationFaName,
        capacity,
        adultPrice,
        childPrice,
        infantPrice,
    } = normalizedFlightItem;

    const handleFlightSelection = async ticket => {
        if (isClickable) {
            handleClick(ticket);
        }
    };

    return (
        <Grid
            className={`minimal-flight-card ${type}`}
            container
            item
            xs={12}
            rowGap={2}
            order={{ xs: 1, md: 2 }}
            onClick={isClickable ? () => handleFlightSelection(flightItem) : null}
        >
            <Grid container item xs={12} justifyContent="space-between">
                <Grid
                    className="minimal-flight-card__labels"
                    container
                    item
                    xs={12}
                    columnGap={{ xs: 0.5, lg: 1 }}
                    rowGap={1}
                    display={{ xs: type === "cart" ? "none" : "flex", md: "flex" }}
                    alignItems="flex-start"
                >
                    {normalizedFlightItem?.type && (
                        <Grid className="minimal-flight-card__label" container item xs="auto">
                            {locale === "en" ? normalizedFlightItem?.type : persianType}
                        </Grid>
                    )}
                    {flightNumber && (
                        <Grid className="minimal-flight-card__label" container item xs="auto">
                            {flightNumber}
                        </Grid>
                    )}
                    {cabinPersian && (
                        <Grid className="minimal-flight-card__label" container item xs="auto">
                            <span>{locale === "en" ? cabin : cabinPersian}</span>
                        </Grid>
                    )}
                    {aircraft && (
                        <Grid className="minimal-flight-card__label" container item xs="auto">
                            <span>{aircraft}</span>
                        </Grid>
                    )}
                </Grid>

                {type === "cart" && (
                    <Grid
                        className="minimal-flight-card__title"
                        component="span"
                        container
                        item
                        xs="auto"
                        display={{ xs: "flex", md: "none" }}
                        justifyContent="center"
                    >
                        {name === "selectedDepartureFlight" ? `${t("departureTicket")}` : `${t("returnTicket")}`}
                    </Grid>
                )}

                {type === "cart" && (
                    <Grid container item xs="auto" md="flex" justifyContent="flex-end">
                        <Grid
                            className="minimal-flight-card__delete"
                            component="span"
                            container
                            item
                            xs="auto"
                            mt={{ md: "10px" }}
                            justifyContent="center"
                            onClick={() => handleDelete(name)}
                        >
                            {t("editTicket")}
                        </Grid>
                    </Grid>
                )}
            </Grid>

            <Grid container item xs={12}>
                <Grid
                    container
                    item
                    xs={3.5}
                    md={3}
                    xl={4}
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent={{ xs: "center", md: "flex-start" }}
                    alignItems={{ xs: "center", md: "center" }}
                >
                    <img className="minimal-flight-card__airline-logo" src={airlineLogo} />
                    <span className="minimal-flight-card__airline-name">{locale === "en" ? airline : airlinePersian}</span>
                </Grid>

                <Grid container item xs={8.5} md={9} xl={8} justifyContent="space-around" alignItems="center">
                    <Grid container item xs="auto" direction="column" alignItems="center">
                        <span className="minimal-flight-card__name">{locale === "en" ? departureEnName : departureFaName}</span>
                        <span className="minimal-flight-card__time">{departureTime}</span>
                    </Grid>
                    <Grid container item xs={6} direction="column" alignItems="center">
                        <TicketArrow icon={<img src={AirplaneSvg} />} />
                        {type === "cart" && (
                            <span className="minimal-flight-card__departure-date">
                                {locale === "en" ? departureDate : convertToJalali(departureDate)}
                            </span>
                        )}
                    </Grid>
                    <Grid container item xs="auto" direction="column" alignItems="center">
                        <span className="minimal-flight-card__name">{locale === "en" ? destinationEnName : destinationFaName}</span>
                        <span className="minimal-flight-card__time">{destinationTime}</span>
                    </Grid>
                </Grid>
            </Grid>
            {!isTwoWay && (
                <>
                    <Divider width="100%" />

                    <Grid container item xs={12} justifyContent="space-between">
                        {type === "cart" ? (
                            <span className="minimal-flight-card__price">{t("price")}:</span>
                        ) : (
                            <span className="minimal-flight-card__capacity">
                                {t("capacity")}: {capacity}
                            </span>
                        )}
                        {type === "suggest" ? (
                            <span className="minimal-flight-card__price">
                                {numberWithCommas(adultPrice)} {t("currency")}
                            </span>
                        ) : (
                            <span className="minimal-flight-card__price">
                                {numberWithCommas(
                                    adultPrice * domesticFlightInformation.personCounter.adult +
                                        childPrice * domesticFlightInformation.personCounter.child +
                                        infantPrice * domesticFlightInformation.personCounter.infant,
                                )}{" "}
                                {t("currency")}
                            </span>
                        )}
                    </Grid>
                </>
            )}
        </Grid>
    );
}

export default FlightCard;

// Revalidate flight
// const [revalidateLoading, setRevalidateLoading] = useState(false);
// const { revalidateBooking } = useRevalidate(generalInformation, setRevalidateLoading);

// const handleFlightSelection = async ticket => {
//     let validationValue;
//     if (ticket.tag === "pr") {
//         await revalidateBooking(ticket?.fare_source_code, "domestic-flight").then(newPrice => {
//             validationValue = newPrice === ticket?.total_price ? true : false;
//         });
//     }
//     if (validationValue || ticket?.tag !== "pr") {
//         handleClick(ticket);
//     }
// };
